import { UUID } from '@cmg/common';
import { FiberManualRecordIcon, Tooltip } from '@cmg/design-system';
import merge from 'lodash/merge';

import { useCheckMyRetailAccess } from '../../../../../common/util/check-access/useCheckMyRetailAccess';
import useCheckOrderBookAccess from '../../../../../common/util/check-access/useCheckOrderBookAccess';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import routeFactory from '../../../../../common/util/routeFactory';
import { ReactRouterDomainNavTabProps } from '../../../../../design-system/react-router/ReactRouterDomainNavTab';
import {
  OfferingStage,
  OfferingStatus,
  OfferingType,
  OrderBookState,
} from '../../../../../graphql';
import { orderBookStateLabels } from '../../../../../types/domain/order-book/constants';
import {
  useOfferingNavbar_OrderBookSubNavItem_OrderBookQuery,
  useOfferingNavbar_OrderBookSubNavItem_OrderBookStatusChangedSubscription,
} from '../graphql';
import { orderBookStateColors } from './orderBookStatus.model';
import { useTotalRetailDemandRoute } from './useTotalRetailDemandRoute';

type Props = {
  offeringId: UUID | undefined;
  // getting the offeringStatus from the parent instead of the local query
  // as the parent query is refetched whenever the offering is updated
  offeringStatus: OfferingStatus | undefined;
  offeringStage: OfferingStage;
  offeringType: OfferingType | undefined;
};

export const useOrderBookRoute = ({
  offeringId,
  offeringStatus,
  offeringType,
}: Props): ReactRouterDomainNavTabProps | undefined => {
  const { canRead: canReadOrderBook } = useCheckOrderBookAccess({ offeringId });

  const { canRead: canReadIndications } =
    useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({ offeringId });
  const { canRead: canReadMyRetailIndication } = useCheckMyRetailAccess({ offeringId });

  const canReadTotalRetailIndication = useTotalRetailDemandRoute({
    offeringId: offeringId ?? '',
  });

  const hasAccessToAtLeastOneSubNavigationTab =
    canReadIndications || canReadTotalRetailIndication || canReadMyRetailIndication;

  const canViewNavItem = !!(
    (canReadOrderBook || canReadMyRetailIndication) &&
    hasAccessToAtLeastOneSubNavigationTab &&
    offeringId &&
    offeringStatus &&
    offeringStatus !== OfferingStatus.Filed &&
    offeringStatus !== OfferingStatus.Withdrawn &&
    // Private Placement demand is served by its own lightweight module instead of the
    // full institutional demand grid, so the Order Book tab is hidden for those deals.
    offeringType !== OfferingType.PrivatePlacement
  );

  const queryResult = useOfferingNavbar_OrderBookSubNavItem_OrderBookQuery({
    variables: {
      id: offeringId ?? '',
    },
    skip: !canViewNavItem,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { loading, data, updateQuery } = queryResult;

  const shouldSubscribeToOrderBookStatus =
    canViewNavItem && data?.orderBook.state === OrderBookState.NeverOpened;

  useOfferingNavbar_OrderBookSubNavItem_OrderBookStatusChangedSubscription({
    variables: {
      offeringId: offeringId ?? '',
    },
    skip: !shouldSubscribeToOrderBookStatus,
    ignoreResults: true,
    onData: ({ data: subscriptionData }) => {
      if (!subscriptionData.data) {
        return;
      }

      updateQuery(prev =>
        merge({}, prev, { orderBook: subscriptionData.data!.orderBookStatusChanged })
      );
    },
  });

  if (!offeringId) {
    return undefined;
  }

  const orderBook = data?.orderBook;
  const status = orderBook ? orderBook.state : OrderBookState.NeverOpened;
  const href = routeFactory.orderBook.getUrlPath({ offeringId });
  const label = orderBookStateLabels[status];
  const color = orderBookStateColors[status];
  if (!canViewNavItem) {
    return undefined;
  }

  return {
    href: href,
    label: (
      <Tooltip title={`Order Book Status: ${label}`} placement="top" arrow>
        <span>Order Book</span>
      </Tooltip>
    ),
    iconPosition: 'start',
    icon: loading ? undefined : <FiberManualRecordIcon color={color} />,
  };
};
